import { Button } from '@invent/wl-ui-kit-next/ui';
import { ArrowRight, RotateCcw } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactTitle,
  DataTable,
  HelpPopover,
  NotPermitted,
  PageHeader,
  STICKY_TABLE_TOP,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  type IDataColumn,
  WorkControls,
  RowOpens,
  PaneControl
} from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import { oversightApi } from '../api/oversight-api';
import type { IOversightResponseRow, IPage, IResponseQuery } from '../api/oversight-contracts';
import { LoadError, PastTheEnd, useAdminData } from '../components/admin-page';
import { useListOrigin } from '../components/list-origin';
import { ParticipantLink, type IListOriginItem } from '../components/participant-link';
import { SEARCH_RULE } from '../components/record-filters';
import { RecycleRunDialog, RecycleRunSaid } from '../components/recycle-run';
import { ResponseStatusChip } from '../components/responses-table';
import { TEAM_PATHS, responsePath } from '../paths';
import { RowNameClicks } from '../components/row-name-click';
import { subjectOf, type IRecycleRun, type IRecycleTarget } from '../recycle-bin';
import { refusalMessage } from '../refusal';
import { useQuerySelection } from './compliance/bulk-decisions';
import { FunnelChoice, FunnelHeader, ParamFunnel } from './compliance/queue-headers';
import { WorkPageHeaderActions, useReadAt } from './lists/work-page/header-actions';
import { SelectionBar } from './lists/work-page/selection-bar';
import {
  WORK_ITEMS_MAX_PAGE,
  WorkPager,
  dropPaging,
  readItemPages,
  useWorkPaging
} from './lists/work-page/pager';
import { RecordSearch } from './record-lists/record-work-page';
import { ParamChip } from './sec-adv/firm-filters';
import { useResponseCatalogue } from './responses-page';

/**
 * The recycle bin as a list (SR-470's D4 and D5; the owner's Q1 (a)) — the responses moved out of
 * play lately, across every record, newest move first: a mistake undone, a colleague's move
 * checked. A rail entry of its own under Cycle rather than a tab of Responses, because the
 * client's staff do not open Responses: they work from a person's, a team's or a firm's record
 * page, which lists the bin's responses and restores one from its survey strip. So a row's
 * participant leads to that record page, where staff carry on.
 *
 * **A row opens its response beside the bin** (SR-530), as every work page opens a row: the bin as
 * drawn the response's column (`useListOrigin` — the response lives at Responses' address, whose
 * own list leaves the bin out), ↑ / ↓ walking it whatever survey each response is of, Esc and Back
 * returning here — or in a new tab, by *Opens in*. A restore on the open response asks whether it
 * is still `recycled`; it is not, so its row is done in the column, and this page reads again on
 * the way back.
 *
 * **The light work page** (`docs/superpowers/specs/2026-10-28-work-pages-design.md`, SR-490): the
 * header with its number, *Refresh* and "?"; the toolbar's filter box; Survey and Cycle as the
 * funnels of their columns, said as chips under the toolbar; the table; the pager. No cards.
 *
 * **The bin's own search** (SR-490): the client asked for a search here, and for a record held
 * only in the bin to be left out of every other search, which it now is (survey-api's lists). `q` is
 * the record search's (`GET /admin/responses?status=recycled&q=`): the responses of the persons,
 * teams and firms it finds, by name, address or number. It reads as typed. The list is the URL —
 * `q`, `survey`, `cycle`, `page`, `size`.
 *
 * **Rows are restored many at once** (`RecycleRunDialog`): ticked per query, one dialog and one
 * reason, then one call per response with the revision this page read. What came back is said
 * above the list and kept until the next press — it names responses the re-read no longer draws —
 * and the list is read again. Without `responses.annotate` the rows still tick and the press is
 * drawn off with `NotPermitted`: the routes' policy, judged by survey-api.
 */
export function AdminRecycleBinPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const survey = params.get('survey') || null;
  const cycle = params.get('cycle') || null;
  const catalogue = useResponseCatalogue();
  const permitted = useCan()('responses.annotate');
  const offId = React.useId();
  const toolbar = useWorkToolbar();
  const paging = useWorkPaging();
  const { page, perPage, pages } = paging;

  const query = binQueryOf(params);
  const asked = JSON.stringify([query, page, perPage, pages]);
  const { data, error, reload, reading } = useAdminData<{
    asked: string;
    page: IPage<IOversightResponseRow>;
  }>(
    async (signal) => {
      try {
        return {
          asked,
          page: await readItemPages(
            (at, pageSize) => oversightApi.listResponses({ ...query, page: at, pageSize }, signal),
            page,
            perPage,
            pages,
            WORK_ITEMS_MAX_PAGE
          )
        };
      } catch (listError) {
        throw new Error(refusalMessage(listError, 'Could not load this page'));
      }
    },
    [asked]
  );
  // The last answer while the next is out; nothing once this query's read has failed.
  const failed = error !== null && !reading;
  const shown = error && data?.asked !== asked ? null : data;
  const rows = shown?.page.items ?? [];
  const readAt = useReadAt(data);
  const selection = useQuerySelection(asked, rows, (row) => row.responseId);
  const opening = useListOrigin('recycle-bin', 'Recycle bin', binOriginItems(rows), BIN_RECHECK);
  /** A row's link: its response beside the bin; its participant's record page in this tab. */
  const follow = (to: string) => (isResponse(to) ? opening.beside(to) : navigate(to));
  const [restoring, setRestoring] = React.useState(false);
  const [ran, setRan] = React.useState<Nullable<IRecycleRun>>(null);
  const [funnelOpen, setFunnelOpen] = React.useState<Nullable<string>>(null);
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);

  /** A filter or search change starts at the top. */
  const write = (name: string, value: Nullable<string>, replace = false) =>
    setParams(
      (current) => {
        const next = dropPaging(new URLSearchParams(current));
        if (value === null || value.trim() === '') next.delete(name);
        else next.set(name, value);
        return next;
      },
      { replace }
    );
  const search = (text: string) => write('q', text.trim(), true);

  /** Cycle codes repeat across surveys, so the funnel narrows with the survey beside it. */
  const cycleCodes = React.useMemo(() => {
    const relevant = catalogue.cycles.filter((row) => survey === null || row.survey.code === survey);
    return [...new Set(relevant.map((row) => row.cycle.code))].sort();
  }, [catalogue.cycles, survey]);
  const surveyName = (code: string) =>
    catalogue.surveys.find((row) => row.code === code)?.name ?? code;

  const funnels: Record<string, IBinFunnel> = {
    survey: {
      label: 'Survey',
      param: 'survey',
      value: survey,
      options: [
        { value: null, label: 'Every survey' },
        ...catalogue.surveys.map((row) => ({ value: row.code, label: row.name }))
      ],
      said: surveyName
    },
    cycle: {
      label: 'Cycle',
      param: 'cycle',
      value: cycle,
      options: [
        { value: null, label: 'Every cycle' },
        ...cycleCodes.map((code) => ({ value: code, label: code }))
      ],
      said: (value) => value
    }
  };
  const funnelBody = (key: string) => {
    const funnel = funnels[key];
    return (
      <FunnelChoice
        label={funnel.label}
        value={funnel.value}
        options={funnel.options}
        onChoose={(value) => {
          write(funnel.param, value);
          setFunnelOpen(null);
        }}
      />
    );
  };
  const funnelHeader = (key: string) => {
    const funnel = funnels[key];
    return (
      <FunnelHeader
        label={funnel.label}
        funnel={
          <ParamFunnel
            code={key}
            label={funnel.label}
            said={funnel.value === null ? null : funnel.said(funnel.value)}
            open={funnelOpen === key}
            onOpenChange={(open) => setFunnelOpen(open ? key : null)}
          >
            {funnelBody(key)}
          </ParamFunnel>
        }
      />
    );
  };
  const chips: ICompactChip[] = Object.entries(funnels).flatMap(([key, funnel]) =>
    funnel.value === null
      ? []
      : [
          {
            key,
            chip: (
              <ParamChip
                words={`${funnel.label}: ${funnel.said(funnel.value)}`}
                body={funnelBody(key)}
                onPress={() => {
                  if (document.querySelector(`[data-funnel="${CSS.escape(key)}"]`) === null) return false;
                  setFunnelOpen(key);
                  return true;
                }}
                onRemove={() => write(funnel.param, null)}
              />
            )
          }
        ]
  );
  const clearAll = () =>
    setParams((current) => {
      const next = new URLSearchParams();
      const kept = current.get('q');
      if (kept) next.set('q', kept);
      return next;
    });

  const narrowed = survey !== null || cycle !== null;
  const tableState: DataTableState | undefined =
    shown === null
      ? failed
        ? { kind: 'error', content: <LoadError message={error ?? ''} /> }
        : { kind: 'loading' }
      : rows.length === 0
        ? {
            kind: 'empty',
            content:
              shown.page.total > 0 ? (
                <PastTheEnd
                  page={shown.page.page}
                  pageSize={shown.page.pageSize}
                  total={shown.page.total}
                  counted={['response', 'responses']}
                  onPage={paging.setPage}
                />
              ) : q !== '' ? (
                `No response in the recycle bin is of a record matching “${q}”${narrowed ? ' with these filters' : ''}.`
              ) : narrowed ? (
                'No response in the recycle bin matches this filter.'
              ) : (
                'The recycle bin is empty.'
              )
          }
        : undefined;

  const columns = binColumns({ survey: funnelHeader('survey'), cycle: funnelHeader('cycle') }, opening.beside);

  const total = shown?.page.total ?? null;
  const refresh = { reading, readAt, onClick: () => void reload() };
  const help = (
    <HelpPopover label="About the recycle bin" align="end">
      <p>
        The responses moved out of play, newest move first. The advisor no longer sees them;
        nothing is deleted. Open a participant for their record page, where a response is moved and
        restored; tick rows here to restore several at once.
      </p>
      <p>
        The search finds the responses of the people, teams and firms it matches. {SEARCH_RULE} The
        record whose every response is here is in no other list or search: find it here.
      </p>
      <p>A column&apos;s funnel narrows the list to one survey or one cycle.</p>
      <p>
        A row opens its response beside the bin by its first icon — the bin stays beside it, ↑ and ↓
        walk it, a restored response is marked Done, Esc comes back — and in a new tab by ↗; a click
        on the row or the survey goes where «Opens in» says.
      </p>
    </HelpPopover>
  );
  const box = (compact: boolean) => (
    <RecordSearch
      q={q}
      label="Filter the recycle bin"
      placeholder="Filter by name, email or CRD…"
      onSearch={search}
      compact={compact}
    />
  );

  return (
    <>
      <PageHeader
        title="Recycle bin"
        row
        actions={
          <WorkPageHeaderActions
            summary={
              total === null
                ? null
                : `${total.toLocaleString('en-US')} ${total === 1 ? 'response' : 'responses'}`
            }
            summaryData={{ 'data-bin-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="Recycle bin, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={chips.length > 0 && <CompactChipLine chips={chips} onClearAll={clearAll} />}
      >
        <CompactTitle>Recycle bin</CompactTitle>
        <CompactDivider />
        {box(true)}
        <span className="flex-1" />
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        <WorkControls end={setControlsEnd}>
          <WorkToolbar
            toolbar={toolbar}
            status={{
              reading,
              count: shown ? `${shown.page.total.toLocaleString('en-US')} found` : undefined
            }}
            search={box(false)}
            quick={null}
            quickCount={0}
            end={<PaneControl prefs={opening.prefs} kind="record" />}
          />
          {chips.length > 0 && <CompactChipLine label="Filters" chips={chips} onClearAll={clearAll} />}
        </WorkControls>

        {ran && <RecycleRunSaid run={ran} />}
        {failed && shown !== null && (
          <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
        )}

        <SelectionBar
          count={selection.rows.length}
          onClear={() => selection.onSelect([])}
          stickyTop={STICKY_TABLE_TOP}
        >
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={!permitted}
            aria-describedby={permitted ? undefined : offId}
            onClick={() => {
              setRan(null);
              setRestoring(true);
            }}
          >
            <RotateCcw className="size-4" aria-hidden />
            Restore ({selection.rows.length})…
          </Button>
          {!permitted && (
            <NotPermitted id={offId} what="restoring a response from the recycle bin" />
          )}
        </SelectionBar>

        {/* A row and its links follow *Opens in* (SR-530): the response beside the bin, the
            participant's record page here; either in a new tab. */}
        <RowNameClicks accept={() => true} open={follow}>
        <DataTable
          label="Recycle bin"
          columns={columns}
          rows={tableState ? null : rows}
          rowKey={(row) => row.responseId}
          onOpen={(row) => opening.open(responsePath(row.responseId))}
          state={tableState}
          busy={reading && shown !== null}
          stickyTop={STICKY_TABLE_TOP}
          selectable={{
            selected: selection.selected,
            onSelect: selection.onSelect,
            name: (row) => `${row.participant.displayName}, ${row.survey.name} ${row.cycle.code}`
          }}
        />
        </RowNameClicks>

        {shown && <WorkPager {...paging.pager} total={shown.page.total} busy={reading} sticky />}
      </div>

      <RecycleRunDialog
        open={restoring}
        onOpenChange={setRestoring}
        move="restore"
        targets={selection.rows.map(targetOf)}
        onRan={async (run) => {
          setRan(run);
          selection.onSelect([]);
          await reload();
        }}
      />
    </>
  );
}

/** The bin's columns — its page's, and the search results page's Recycle bin card (SR-490) —
 *  with each page's own Survey and Cycle headers (the page's carry their funnels); `beside` opens a
 *  response beside the list the rows are. On the results page a participant's link is a list's
 *  (`fromList`), so the record returns there. */
export function binColumns(
  heads: { survey: React.ReactNode; cycle: React.ReactNode },
  beside: (to: string) => void,
  fromList = false
): IDataColumn<IOversightResponseRow>[] {
  return [
    {
      // Both ways in, first on a row (SR-530): the response beside the list, and in a new tab.
      // None the viewer arranges or hides.
      id: 'open',
      header: <span className="sr-only">Open</span>,
      label: 'Open',
      className: 'w-0 whitespace-nowrap',
      cell: (row) => {
        const name = `${row.participant.displayName} — ${row.survey.code} ${row.cycle.code}`;
        const to = responsePath(row.responseId);
        return <RowOpens name={name} onPane={() => beside(to)} to={to} tabWords={`Open ${name} in a new tab`} />;
      }
    },
    {
      id: 'participant',
      header: 'Participant',
      cell: (row) => <ParticipantLink participant={row.participant} fromList={fromList} />
    },
    {
      id: 'survey',
      header: heads.survey,
      label: 'Survey',
      // The response: its plain click is caught by the table's `RowNameClicks` and opens it beside
      // the list; Ctrl/⌘ or the middle button take the address.
      cell: (row) => (
        <Link
          to={responsePath(row.responseId)}
          className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
        >
          {row.survey.name}
        </Link>
      )
    },
    {
      id: 'cycle',
      header: heads.cycle,
      label: 'Cycle',
      cell: (row) => (
        <>
          {row.cycle.code}
          {row.path && <span className="ml-1 text-xs text-muted-foreground">{row.path.code}</span>}
        </>
      )
    },
    {
      id: 'from',
      header: 'Was',
      cell: (row) => row.recycled && <ResponseStatusChip status={row.recycled.from} />
    },
    {
      id: 'moved',
      header: 'Moved',
      className: 'text-xs whitespace-nowrap text-muted-foreground',
      // A row of the legacy platform's bin carries no date; an API before the bin, no `recycled`.
      cell: (row) =>
        row.recycled && (row.recycled.at ? formatDateTime(row.recycled.at) : 'legacy platform')
    },
    {
      id: 'by',
      header: 'By',
      className: 'text-xs text-muted-foreground',
      cell: (row) => row.recycled && (row.recycled.by?.email ?? '—')
    },
    {
      id: 'open',
      header: '',
      label: 'Open',
      headClassName: 'w-10',
      cell: () => <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
    }
  ];
}

/** A column's funnel: its URL key, the value set, its positions and how a value is said. */
interface IBinFunnel {
  label: string;
  param: string;
  value: Nullable<string>;
  options: { value: Nullable<string>; label: string }[];
  said: (value: string) => string;
}

/** The URL's search and filters as the bin's query: the record search's words, the survey and the
 *  cycle, narrowed to `recycled`. */
function binQueryOf(params: URLSearchParams): IResponseQuery {
  return {
    q: params.get('q') || undefined,
    surveyCode: params.get('survey') ?? undefined,
    cycleCode: params.get('cycle') ?? undefined,
    status: ['recycled']
  };
}

/** The bin's question of a response opened from it, after a write: is it still `recycled`. */
export const BIN_RECHECK = { kind: 'bin' } as const;

/** Whether a link is a response's — a bin row's survey — rather than its participant's record. */
export function isResponse(to: string): boolean {
  return to.startsWith(`${TEAM_PATHS.responses}/`);
}

/** The bin's rows as a response's column: who, which survey and cycle. */
export function binOriginItems(rows: readonly IOversightResponseRow[]): IListOriginItem[] {
  return rows.map((row) => ({
    key: row.responseId,
    to: responsePath(row.responseId),
    primary: row.participant.displayName,
    secondary: `${row.survey.code} ${row.cycle.code}`
  }));
}

/** A row as the run sends it — the revision this page read, and whose response it is. */
function targetOf(row: IOversightResponseRow): IRecycleTarget {
  return { responseId: row.responseId, revision: row.revision, subject: subjectOf(row) };
}
